import { isEmpty } from "lodash/fp";
import { useNavigate } from "react-router-dom";

import { Stack, Button } from "@mui/material";

import DashboardPlaceholder from "@app/analytics/assets/DashboardPlaceholder.png";
import TeamsPlaceholder from "@app/analytics/assets/TeamsPlaceholder.png";
import Placeholder from "@app/analytics/components/Placeholder";
import Table from "@app/analytics/components/Table";
import TableBody from "@app/analytics/components/Table/TableBody";
import TableHead from "@app/analytics/components/Table/TableHead";
import UpsellTooltipWrapper from "@app/analytics/components/UpsellTooltipWrapper";
import useDataTable, { HeadCell } from "@app/common/hooks/useDataTable";
import ROUTES from "@app/common/routes";
import { EnterpriseWorkspaceTeam } from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { isWorkspaceAdmin } from "@app/common/workspaces/workspaceUtils";

import TeamsTableRow from "./TeamsTableRow";

interface TeamsTableProps {
  setAddTeamOpen: (teamOpen: boolean) => void;
}

const HEAD_CELLS: HeadCell<EnterpriseWorkspaceTeam>[] = [
  {
    id: "teamName",
    label: "Team Name",
    colSpan: 2,
    sortable: true,
    sortBy: (team) => team.name,
  },
  {
    id: "teamMembers",
    label: "Team Members",
    sortable: true,
    sortBy: (team) => team.users.length,
  },
  {
    id: "manager",
    label: "Manager(s)",
    sortable: false,
  },
];

const USER_HEAD_CELLS: HeadCell<EnterpriseWorkspaceTeam>[] = [
  ...HEAD_CELLS,
  {
    id: "yourRole",
    label: "Your Role",
    sortable: true,
    sortBy: (team) => team.role,
  },
  {
    id: "actions",
    label: "",
    sortable: false,
  },
];

const UserEmptyState = () => {
  const navigate = useNavigate();

  return (
    <Placeholder
      image={DashboardPlaceholder}
      title="There's still lots to see!"
      subtitle="While you wait for a team invitation, you can explore your individual data in your Dashboard and beyond."
    >
      <Button
        variant="contained"
        onClick={() => navigate(ROUTES.LANDING_PAGE)}
        sx={{ width: "200px", height: "48px" }}
      >
        Show me my Dashboard
      </Button>
    </Placeholder>
  );
};

const AdminEmptyState = ({
  setAddTeamOpen,
  workspaceIsInactive,
}: {
  setAddTeamOpen: (teamOpen: boolean) => void;
  workspaceIsInactive: boolean;
}) => {
  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{
        py: "104px",
        border: "1px solid",
        borderTop: "0px",
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
        borderColor: "shades.neutral.100",
      }}
    >
      <Placeholder
        image={TeamsPlaceholder}
        title="No teams"
        subtitle="Teams allow your organization to customize settings for different groups of people."
      >
        <UpsellTooltipWrapper show={workspaceIsInactive}>
          <Button
            disabled={workspaceIsInactive}
            variant="contained"
            onClick={() => setAddTeamOpen(true)}
            sx={{ width: "200px", height: "48px" }}
          >
            Create new team
          </Button>
        </UpsellTooltipWrapper>
      </Placeholder>
    </Stack>
  );
};

const TeamsTable = ({ setAddTeamOpen }: TeamsTableProps) => {
  const { removeUsersFromTeam, workspace, workspaceIsInactive, workspaceUser } =
    useWorkspace();
  const isAdmin = isWorkspaceAdmin(workspaceUser);

  const headCells = isAdmin ? HEAD_CELLS : USER_HEAD_CELLS;

  const { handleSort, order, orderBy, tableRows } = useDataTable(
    workspace.teams,
    headCells,
    { defaultOrder: "asc" }
  );

  const handleLeaveTeam = (teamId: string) =>
    removeUsersFromTeam.mutate({ teamId, userIds: [workspaceUser.userId] });

  if (isEmpty(tableRows) && !isAdmin) {
    return <UserEmptyState />;
  }

  return (
    <>
      <Table
        sx={{
          "&.MuiTable-root": {
            borderBottomLeftRadius: isEmpty(tableRows) ? "0px" : "8px",
            borderBottomRightRadius: isEmpty(tableRows) ? "0px" : "8px",
          },
          width: "100%",
        }}
      >
        <TableHead
          headCells={headCells}
          onSort={handleSort}
          order={order}
          orderBy={orderBy}
        />
        <TableBody>
          {tableRows.map((team) => (
            <TeamsTableRow
              key={team.id}
              currentUser={workspaceUser}
              onLeaveTeam={handleLeaveTeam}
              team={team}
              workspaceId={workspace.id}
            />
          ))}
        </TableBody>
      </Table>
      {isEmpty(tableRows) && (
        <AdminEmptyState
          setAddTeamOpen={setAddTeamOpen}
          workspaceIsInactive={workspaceIsInactive}
        />
      )}
    </>
  );
};

export default TeamsTable;
